import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus, Repeat, Square } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input, Label, NativeSelect } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { PageHeader } from "@/components/common";
import { api } from "@/lib/api";
import { dateTime, money, type Money } from "@/lib/format";
import { parseEuroToMinor, parsePct } from "@/lib/money-input";

interface Option {
  id: string;
  code: string;
  name: string;
}
interface FeeOptions {
  markets: (Option & { currency: string })[];
  categories: Option[];
  services: Option[];
}
type CatalogData = FeeOptions;
const nameOf = (x: Option) => x.name;

interface FeeRule {
  id: string;
  marketId: string;
  marketCode: string;
  kind: string;
  payer: string;
  name: string | null;
  scope: {
    categoryId: string | null;
    categoryCode: string | null;
    serviceId: string | null;
    serviceCode: string | null;
    providerType: "individual" | "company" | null;
    customerId: string | null;
    customerEmail: string | null;
    providerId: string | null;
    providerName: string | null;
  };
  rateBps: number | null;
  fixed: Money | null;
  providerShareBps: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  state: "active" | "scheduled" | "ended";
}

const KINDS: Record<string, { label: string; hint: string }> = {
  commission: {
    label: "Komisyon",
    hint: "Müşteri öder: liste fiyatı = hizmet veren fiyatı + komisyon (K-012).",
  },
  urgent_fee: {
    label: "Acil çağrı ücreti",
    hint: "Hizmet veren kabul edince çekilir; payı hizmet verene, kalanı Poji'ye (K-050).",
  },
  service_fee: { label: "Hizmet bedeli", hint: "İş başına müşteriden alınan ek ücret." },
  trust_support_fee: {
    label: "Güven ve destek ücreti",
    hint: "İş başına müşteriden alınan ek ücret.",
  },
};
const kindLabel = (k: string) => KINDS[k]?.label ?? k;
const pct = (bps: number | null) =>
  bps === null ? "—" : `%${(bps / 100).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}`;
/** "12,5" → 1250 bps; geçersizse null */
const bpsText = (bps: number | null) => (bps === null ? "" : String(bps / 100).replace(".", ","));

function scopeText(r: FeeRule, data: CatalogData | undefined) {
  const s = r.scope;
  const parts: string[] = [];
  if (s.serviceId) {
    const x = data?.services.find((v) => v.id === s.serviceId);
    parts.push(`Hizmet: ${x ? nameOf(x) : s.serviceCode}`);
  }
  if (s.categoryId) {
    const x = data?.categories.find((v) => v.id === s.categoryId);
    parts.push(`Kategori: ${x ? nameOf(x) : s.categoryCode}`);
  }
  if (s.providerType) parts.push(s.providerType === "company" ? "Şirketler" : "Bireyler");
  if (s.customerId) parts.push(`Müşteri: ${s.customerEmail ?? s.customerId.slice(0, 8)}`);
  if (s.providerId) parts.push(`Hizmet veren: ${s.providerName ?? s.providerId.slice(0, 8)}`);
  return parts.length ? parts.join(" · ") : "Tüm pazar (varsayılan)";
}

function valueText(r: FeeRule) {
  if (r.kind === "urgent_fee")
    return `${money(r.fixed)} · hizmet verene ${pct(r.providerShareBps)}`;
  const p: string[] = [];
  if (r.rateBps !== null) p.push(pct(r.rateBps));
  if (r.fixed) p.push(money(r.fixed));
  return p.join(" + ") || "—";
}

const STATE: Record<FeeRule["state"], [string, "success" | "warning" | "secondary"]> = {
  active: ["Geçerli", "success"],
  scheduled: ["Planlandı", "warning"],
  ended: ["Bitti", "secondary"],
};

/** Yeni kural ya da var olan kuralı yeni değerle değiştirme (eski kural yenisi başlayınca kapanır) */
function RuleDialog({ data, replaces }: { data: CatalogData; replaces?: FeeRule }) {
  const qc = useQueryClient();
  const init = React.useCallback(
    () => ({
      marketId: replaces?.marketId ?? data.markets[0]?.id ?? "",
      kind: replaces?.kind ?? "commission",
      scope: replaces?.scope.serviceId
        ? "service"
        : replaces?.scope.categoryId
          ? "category"
          : "market",
      categoryId: replaces?.scope.categoryId ?? "",
      serviceId: replaces?.scope.serviceId ?? "",
      providerType: replaces?.scope.providerType ?? "",
      rate: bpsText(replaces?.rateBps ?? null),
      fixed: replaces?.fixed
        ? (Number(replaces.fixed.amountMinor) / 100).toString().replace(".", ",")
        : "",
      share: bpsText(replaces?.providerShareBps ?? null),
      when: "now" as "now" | "later",
      at: "",
      name: "",
    }),
    [replaces, data],
  );
  const [f, setF] = React.useState(init);
  const set = (p: Partial<ReturnType<typeof init>>) => setF((x) => ({ ...x, ...p }));
  const urgent = f.kind === "urgent_fee";
  const rateBps = f.rate.trim() ? parsePct(f.rate) : null;
  const fixedMinor = f.fixed.trim() ? parseEuroToMinor(f.fixed) : null;
  const shareBps = f.share.trim() ? parsePct(f.share) : null;
  const at = f.when === "later" && f.at ? new Date(f.at) : null;

  const valid =
    Boolean(f.marketId) &&
    (f.scope !== "category" || Boolean(f.categoryId)) &&
    (f.scope !== "service" || Boolean(f.serviceId)) &&
    (!f.rate.trim() || rateBps !== null) &&
    (!f.fixed.trim() || fixedMinor !== null) &&
    (urgent ? fixedMinor !== null && shareBps !== null : rateBps !== null || fixedMinor !== null) &&
    (f.when === "now" || (at !== null && at.getTime() > Date.now()));

  const locked = Boolean(replaces);
  return (
    <ActionDialog
      wide
      onOpen={() => setF(init())}
      trigger={
        replaces ? (
          <Button size="sm" variant="outline">
            <Repeat /> Değiştir
          </Button>
        ) : (
          <Button size="sm">
            <Plus /> Yeni kural
          </Button>
        )
      }
      title={replaces ? `${kindLabel(replaces.kind)}: yeni değer` : "Yeni ücret kuralı"}
      description={
        replaces
          ? "Kural yerinde değiştirilmez: yeni değer seçtiğiniz anda başlar, eskisi o anda biter. Açık işler kendi tutarını korur."
          : "Kural yalnızca yeni işlere uygulanır. En dar kapsam kazanır: hizmet > kategori > tüm pazar."
      }
      confirmLabel={replaces ? "Yeni değeri kaydet" : "Kuralı ekle"}
      canSubmit={valid}
      success="Ücret kuralı kaydedildi."
      onSubmit={async (key) => {
        await api("/v1/admin/fees", {
          method: "POST",
          idempotencyKey: key,
          body: {
            marketId: f.marketId,
            kind: f.kind,
            categoryId: f.scope === "category" ? f.categoryId : null,
            serviceId: f.scope === "service" ? f.serviceId : null,
            providerType: f.providerType || null,
            rateBps: urgent ? null : rateBps,
            fixedMinor,
            providerShareBps: urgent ? shareBps : null,
            effectiveFrom: at ? at.toISOString() : null,
            name: f.name.trim() || null,
            replacesId: replaces?.id ?? null,
          },
        });
        await qc.invalidateQueries({ queryKey: ["admin-fees"] });
      }}
    >
      <div className="grid gap-4">
        {replaces && (
          <div className="rounded-lg bg-muted px-3 py-2 text-sm">
            Şu anki: <b>{valueText(replaces)}</b> · {scopeText(replaces, data)} ·{" "}
            {replaces.marketCode}
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="fee-market">Ülke</Label>
            <NativeSelect
              id="fee-market"
              disabled={locked}
              value={f.marketId}
              onChange={(e) => set({ marketId: e.target.value })}
            >
              {data.markets.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.currency})
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="fee-kind">Tür</Label>
            <NativeSelect
              id="fee-kind"
              disabled={locked}
              value={f.kind}
              onChange={(e) => set({ kind: e.target.value })}
            >
              {Object.entries(KINDS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>
        <p className="-mt-2 text-xs text-muted-foreground">{KINDS[f.kind]?.hint}</p>

        {!locked && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="fee-scope">Neye uygulanır</Label>
              <NativeSelect
                id="fee-scope"
                value={f.scope}
                onChange={(e) => set({ scope: e.target.value })}
              >
                <option value="market">Tüm hizmetler</option>
                <option value="category">Bir kategori</option>
                <option value="service">Bir hizmet</option>
              </NativeSelect>
            </div>
            {f.scope === "category" && (
              <div className="grid gap-2">
                <Label htmlFor="fee-cat">Kategori</Label>
                <NativeSelect
                  id="fee-cat"
                  value={f.categoryId}
                  onChange={(e) => set({ categoryId: e.target.value })}
                >
                  <option value="">Seçin</option>
                  {data.categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {nameOf(c)}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            )}
            {f.scope === "service" && (
              <div className="grid gap-2">
                <Label htmlFor="fee-svc">Hizmet</Label>
                <NativeSelect
                  id="fee-svc"
                  value={f.serviceId}
                  onChange={(e) => set({ serviceId: e.target.value })}
                >
                  <option value="">Seçin</option>
                  {data.services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {nameOf(s)}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="fee-ptype">Hizmet veren türü</Label>
              <NativeSelect
                id="fee-ptype"
                value={f.providerType}
                onChange={(e) => set({ providerType: e.target.value })}
              >
                <option value="">Hepsi</option>
                <option value="individual">Yalnızca bireyler</option>
                <option value="company">Yalnızca şirketler</option>
              </NativeSelect>
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {!urgent && (
            <div className="grid gap-2">
              <Label htmlFor="fee-rate">Oran (%)</Label>
              <Input
                id="fee-rate"
                inputMode="decimal"
                placeholder="ör. 20"
                value={f.rate}
                aria-invalid={Boolean(f.rate.trim()) && rateBps === null}
                onChange={(e) => set({ rate: e.target.value })}
              />
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="fee-fixed">
              {urgent ? "Tutar (€)" : "Sabit tutar (€, isteğe bağlı)"}
            </Label>
            <Input
              id="fee-fixed"
              inputMode="decimal"
              placeholder="ör. 25"
              value={f.fixed}
              aria-invalid={Boolean(f.fixed.trim()) && fixedMinor === null}
              onChange={(e) => set({ fixed: e.target.value })}
            />
          </div>
          {urgent && (
            <div className="grid gap-2">
              <Label htmlFor="fee-share">Hizmet verenin payı (%)</Label>
              <Input
                id="fee-share"
                inputMode="decimal"
                placeholder="ör. 60"
                value={f.share}
                aria-invalid={Boolean(f.share.trim()) && shareBps === null}
                onChange={(e) => set({ share: e.target.value })}
              />
            </div>
          )}
        </div>
        {urgent && fixedMinor !== null && shareBps !== null && (
          <p className="text-sm text-muted-foreground">
            Müşteri {money({ amountMinor: fixedMinor, currency: "EUR" })} öder → hizmet verene{" "}
            {money({
              amountMinor: String((BigInt(fixedMinor) * BigInt(shareBps)) / 10000n),
              currency: "EUR",
            })}
            , Poji'ye kalanı.
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="fee-when">Ne zaman başlasın</Label>
            <NativeSelect
              id="fee-when"
              value={f.when}
              onChange={(e) => set({ when: e.target.value as "now" | "later" })}
            >
              <option value="now">Hemen</option>
              <option value="later">İleri bir tarihte</option>
            </NativeSelect>
          </div>
          {f.when === "later" && (
            <div className="grid gap-2">
              <Label htmlFor="fee-at">Başlangıç (Malta saati)</Label>
              <Input
                id="fee-at"
                type="datetime-local"
                value={f.at}
                onChange={(e) => set({ at: e.target.value })}
              />
            </div>
          )}
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="fee-name">İç not (isteğe bağlı)</Label>
            <Input
              id="fee-name"
              maxLength={120}
              placeholder="ör. Yaz kampanyası temizlikte %15"
              value={f.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </div>
        </div>
      </div>
    </ActionDialog>
  );
}

function EndDialog({ rule }: { rule: FeeRule }) {
  const qc = useQueryClient();
  return (
    <ActionDialog
      trigger={
        <Button size="sm" variant="ghost">
          <Square /> Bitir
        </Button>
      }
      title="Kuralı bitir"
      description={
        rule.kind === "urgent_fee" && !rule.scope.serviceId && !rule.scope.categoryId
          ? "Bu, pazarın acil çağrı ücreti. Biterse yerine kural eklenene kadar bu pazarda acil çağrı açılamaz."
          : "Kural hemen biter; bundan sonra açılan işlere daha genel kural uygulanır. Açık işler etkilenmez."
      }
      confirmLabel="Bitir"
      confirmVariant="destructive"
      success="Kural bitirildi."
      onSubmit={async (key) => {
        await api(`/v1/admin/fees/${rule.id}/end`, {
          method: "POST",
          idempotencyKey: key,
          body: {},
        });
        await qc.invalidateQueries({ queryKey: ["admin-fees"] });
      }}
    >
      <div className="rounded-lg bg-muted px-3 py-2 text-sm">
        {kindLabel(rule.kind)} · {valueText(rule)} · {rule.marketCode}
      </div>
    </ActionDialog>
  );
}

const isBaseCommission = (r: FeeRule) =>
  r.kind === "commission" && Object.values(r.scope).every((v) => v === null);

export function FeesPage() {
  const catalog = useQuery({
    queryKey: ["admin-fee-options"],
    queryFn: () => api<FeeOptions>("/v1/admin/fees/options"),
  });
  const q = useQuery({
    queryKey: ["admin-fees"],
    queryFn: () => api<{ rules: FeeRule[] }>("/v1/admin/fees"),
  });
  const [show, setShow] = React.useState<"current" | "all">("current");
  const rules = (q.data?.rules ?? []).filter((r) => show === "all" || r.state !== "ended");

  const columns: ColumnDef<FeeRule, unknown>[] = [
    {
      id: "kind",
      header: "Tür",
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{kindLabel(row.original.kind)}</div>
          {row.original.name && (
            <div className="text-xs text-muted-foreground">{row.original.name}</div>
          )}
        </div>
      ),
    },
    {
      id: "scope",
      header: "Kapsam",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.marketCode} · {scopeText(row.original, catalog.data)}
        </span>
      ),
    },
    {
      id: "value",
      header: "Değer",
      cell: ({ row }) => <span className="tabular font-medium">{valueText(row.original)}</span>,
    },
    {
      id: "period",
      header: "Geçerlilik",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {dateTime(row.original.effectiveFrom)} →{" "}
          {row.original.effectiveTo ? dateTime(row.original.effectiveTo) : "süresiz"}
        </span>
      ),
    },
    {
      id: "state",
      header: "Durum",
      cell: ({ row }) => {
        const [t, v] = STATE[row.original.state];
        return <Badge variant={v}>{t}</Badge>;
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const r = row.original;
        if (r.state === "ended" || !catalog.data) return null;
        // müşteriye/hizmet verene özel kurallar (K-016) bu ekrandan değiştirilmez
        const special = r.scope.customerId || r.scope.providerId;
        return (
          <div className="flex justify-end gap-1">
            {!special && r.payer === "customer" && <RuleDialog data={catalog.data} replaces={r} />}
            {!isBaseCommission(r) && <EndDialog rule={r} />}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Ücret kuralları"
        description="Komisyon, acil çağrı ücreti ve ek ücretler. Değişiklik yalnızca yeni işlere uygulanır; eski kurallar geçmiş olarak kalır."
        actions={
          <>
            <NativeSelect
              aria-label="Gösterim"
              value={show}
              onChange={(e) => setShow(e.target.value as typeof show)}
            >
              <option value="current">Geçerli ve planlanan</option>
              <option value="all">Geçmiş dahil</option>
            </NativeSelect>
            {catalog.data && <RuleDialog data={catalog.data} />}
          </>
        }
      />
      <DataTable
        columns={columns}
        data={q.data ? rules : undefined}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Kural yok."
      />
    </>
  );
}
